import React, { useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { PetProvider } from './contexts/PetContext';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import DatePickerProvider from './contexts/DatePickerProvider';
import { LayoutProvider, useLayout } from './contexts/LayoutContext';
import { ErrorProvider } from './contexts/ErrorContext';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider } from './components/NotificationSystem';
import { VoiceAssistantController } from './components/VoiceAssistant';
import { Layout } from './components/Layout';
import { useResponsive } from './config/layoutConfig';
import PrivateRoute from './components/PrivateRoute';
import { AdminLayout } from './components/AdminLayout';
import { ToastProvider } from './components/Toast/ToastProvider';
import { DashboardSkeleton } from './components/Loading/SkeletonLoader';
import { AppointmentDialogProvider, useAppointmentDialog } from './pages/Calendar/AppointmentDialogContext';
import ScheduleAppointmentDialog from './components/QuickActions/ScheduleAppointmentDialog';
import { auth } from './config/firebase';
import NotificationIntegrationService from './services/notificationIntegrationService';
import { preloadCriticalRoutes, trackPerformance, addResourceHints } from './utils/performance';
import { logger } from './services/logger';

// Import centralized lazy components
import {
  // Auth components
  LazyLogin,
  LazyRegister,
  LazyForgotPassword,
  
  // Email verification
  LazyVerifyEmail,

  // Trial gateway (Phase 3 — F&F invite redemption + paid Stripe entry)
  LazyTrial,

  // Post-checkout onboarding
  LazyPostCheckoutOnboarding,

  // Home dashboard (iOS-synced)
  LazyHomeDashboard,

  // Main pages
  LazyCalendar,
  LazyDocuments,
  LazyAlerts,
  LazyProfileSettings,
  LazyCareTeam,
  LazyInbox,
  LazyPetExpenses,
  LazyEmailDiagnostics,
  LazyInsurance,
  LazyLabResultsPage,
  LazyNotifications,
  LazyAskSuprie,
  
  // Forms
  LazyPetForm,
  LazyPetProfilePage,
  LazyPetGenetics,
  
  // Health Records
  LazyHealthInsightsRedesigned,
  // LazyMedications, // REMOVED: Medications now in Care Plan only
  LazyCarePlanPage,
  
  
  // Care Team
  LazyCareTeamAdd,
  LazyCareTeamFind,
  
  // Expenses
  LazyExpenseAdd,
  LazyExpenseReports,
  LazyExpenseInsurance,
  
  // Heavy components
  LazyVoiceAssistant,
} from './components/LazyComponents';

// Import enhanced loading components
import {
  PageLoader,
  HeavyComponentLoader,
} from './components/Loading/ChunkLoader';

// Wrap Routes with Sentry for per-route performance transactions
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Admin pages - lazy loaded
const LazyMigrateMedications = lazy(() => import('./pages/Admin/MigrateMedications'));
const LazyAdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
// import { patentIntegration } from './integration/patentIntegration'; // Removed - patent integration deleted
// Import email test utilities (makes them available in browser console)
// Enhanced loading component for lazy-loaded routes
const _OldPageLoader = () => (
  <Box sx={{ p: 3 }}>
    <DashboardSkeleton />
  </Box>
);

// Global EventForm component to handle appointments from any page
/**
 * GlobalEventForm — single global mount of the appointment dialog.
 *
 * Wired through `AppointmentDialogContext` so any page can open it via
 * `useAppointmentDialog().openDialog(date, type, initialData)`. This wrapper
 * only handles `type: 'appointment'`. Reminder and medication entry points
 * use their own dedicated dialogs (ReminderDialog / Care Plan).
 *
 * Replaced the legacy ~2700-line EventForm — save logic now lives inside
 * ScheduleAppointmentDialog, so this wrapper is purely state plumbing.
 */
const GlobalEventForm: React.FC = () => {
  const { open, closeDialog, initialEventData, selectedDate } = useAppointmentDialog();

  return (
    <ScheduleAppointmentDialog
      open={open}
      onClose={closeDialog}
      eventToEdit={initialEventData?.id ? initialEventData : null}
      initialDate={selectedDate}
    />
  );
};

// Main App Router component that uses layout context
const AppRouter: React.FC = () => {
  useTheme();
  // FIXED: Use standardized responsive utilities
  useResponsive();
  
  // FIXED: Use layout context instead of static configuration
  useLayout();

  return (
    <Router>
      <AuthProvider>
        <PetProvider>
          <DatePickerProvider>
            <AppointmentDialogProvider>
              <AppWithOnboarding />
            </AppointmentDialogProvider>
          </DatePickerProvider>
        </PetProvider>
      </AuthProvider>
    </Router>
  );
};

// Top-level routed app. (Phase 4 retired the EnhancedOnboarding overlay; the
// /trial → /onboarding flow is now the single canonical onboarding journey.)
const AppWithOnboarding: React.FC = () => {
  // Create ref for VoiceAssistant
  const voiceAssistantRef = useRef<VoiceAssistantController>(null);
  
  // FIXED: Use original Layout component with header and sidebar
  const LayoutComponent = Layout;
  
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <SentryRoutes>
                    {/* Public Routes */}
                    <Route path="/login" element={
                      <Suspense fallback={<PageLoader />}>
                        <LazyLogin />
                      </Suspense>
                    } />
                    <Route path="/register" element={
                      <Suspense fallback={<PageLoader />}>
                        <LazyRegister />
                      </Suspense>
                    } />
                    <Route path="/forgot-password" element={
                      <Suspense fallback={<PageLoader />}>
                        <LazyForgotPassword />
                      </Suspense>
                    } />
                    <Route path="/verify-email" element={
                      <Suspense fallback={<PageLoader />}>
                        <LazyVerifyEmail />
                      </Suspense>
                    } />
                    {/* Trial gateway — authenticated, full-screen (no Layout).
                        Auto-redeems an invite token from URL/sessionStorage
                        OR offers manual code entry / Stripe checkout. */}
                    <Route path="/trial" element={
                      <PrivateRoute>
                        <Suspense fallback={<PageLoader />}>
                          <LazyTrial />
                        </Suspense>
                      </PrivateRoute>
                    } />
                    {/* Post-checkout onboarding — authenticated, full-screen (no Layout). */}
                    <Route path="/onboarding" element={
                      <PrivateRoute>
                        <Suspense fallback={<PageLoader />}>
                          <LazyPostCheckoutOnboarding />
                        </Suspense>
                      </PrivateRoute>
                    } />
                    {/* Admin (protected by AdminLayout — admin role only) */}
                    <Route path="/admin" element={
                      <PrivateRoute>
                        <AdminLayout />
                      </PrivateRoute>
                    }>
                      <Route index element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyAdminDashboard />
                        </Suspense>
                      } />
                      <Route path="migrate-medications" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyMigrateMedications />
                        </Suspense>
                      } />
                    </Route>
                    {/* Removed patent-test and ml-test routes - components deleted */}
                    
                    {/* Protected Routes */}
                    <Route path="/" element={<PrivateRoute><LayoutComponent /></PrivateRoute>}>
                      <Route index element={<Navigate to="/home" replace />} />
                      <Route path="home" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyHomeDashboard />
                        </Suspense>
                      } />
                      <Route path="dashboard" element={<Navigate to="/home" replace />} />
                      <Route path="calendar" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyCalendar />
                        </Suspense>
                      } />
                      <Route path="documents" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyDocuments />
                        </Suspense>
                      } />
                      <Route path="alerts" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyAlerts />
                        </Suspense>
                      } />
                      <Route path="profile" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyProfileSettings />
                        </Suspense>
                      } />
                      <Route path="pets/add" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyPetForm />
                        </Suspense>
                      } />
                      <Route path="pets/edit/:petId" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyPetProfilePage />
                        </Suspense>
                      } />
                      <Route path="pets/:petId/genetics" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyPetGenetics />
                        </Suspense>
                      } />
                      <Route path="profile-settings" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyProfileSettings />
                        </Suspense>
                      } />
                      <Route path="care-team" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyCareTeam />
                        </Suspense>
                      } />
                      <Route path="care-team/add" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyCareTeamAdd />
                        </Suspense>
                      } />
                      <Route path="care-team/find" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyCareTeamFind />
                        </Suspense>
                      } />
                      <Route path="email-diagnostics" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyEmailDiagnostics />
                        </Suspense>
                      } />
                      <Route path="inbox" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyInbox />
                        </Suspense>
                      } />
                      <Route path="pet-expenses" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyPetExpenses />
                        </Suspense>
                      } />
                      <Route path="expenses/add" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyExpenseAdd />
                        </Suspense>
                      } />
                      <Route path="expenses/reports" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyExpenseReports />
                        </Suspense>
                      } />
                      <Route path="expenses/insurance" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyExpenseInsurance />
                        </Suspense>
                      } />
                      <Route path="insurance" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyInsurance />
                        </Suspense>
                      } />
                      <Route path="lab-results" element={
                        <Suspense fallback={<HeavyComponentLoader />}>
                          <LazyLabResultsPage />
                        </Suspense>
                      } />
                      <Route path="notifications" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyNotifications />
                        </Suspense>
                      } />
                      
                      {/* Ask Suprie - AI Chat Assistant */}
                      <Route path="ask-suprie" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyAskSuprie />
                        </Suspense>
                      } />

                      {/* Health Insights Route - REDESIGNED 3-Tab Interface */}
                      <Route path="health" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyHealthInsightsRedesigned />
                        </Suspense>
                      } />
                      <Route path="health-insights" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyHealthInsightsRedesigned />
                        </Suspense>
                      } />
                      
                      {/* Care Plan Route - Dedicated Care Management Page */}
                      <Route path="care-plan" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyCarePlanPage />
                        </Suspense>
                      } />
                      
                      {/* Health Records Routes - Redirected to Health Insights */}
                      <Route path="health-records" element={<Navigate to="/health-insights?tab=timeline" replace />} />
                      <Route path="health-records/*" element={<Navigate to="/health-insights?tab=timeline" replace />} />
                      
                      {/* Health Insights Timeline Route - Pet specific */}
                      <Route path="pets/:petId/health-dashboard" element={
                        <Suspense fallback={<PageLoader />}>
                          <LazyHealthInsightsRedesigned />
                        </Suspense>
                      } />
                      
                      {/* Admin Routes */}
                    </Route>
                    
                    {/* Catch all route - redirect to login for public users */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </SentryRoutes>
                </Suspense>
                
                {/* Global EventForm for appointments - available on all pages */}
                <GlobalEventForm />
                
                {/* Global VoiceAssistant - available on all pages */}
                <Suspense fallback={<HeavyComponentLoader />}>
                  <LazyVoiceAssistant ref={voiceAssistantRef} />
                </Suspense>
              </>
            );
          };

// Main App component with all providers
const App: React.FC = () => {
  const appLog = logger.createScope('App');
  appLog.debug('App component rendering');
  // Removed dev-only auto sign-out

  // Initialize performance monitoring
  React.useEffect(() => {
    appLog.info('Performance monitoring initialized');
    // Add resource hints for faster loading
    addResourceHints();
    
    // Track performance metrics
    trackPerformance();
    
    // Preload critical routes after initial render
    setTimeout(() => {
      preloadCriticalRoutes();
    }, 2000); // Delay to not interfere with initial load
  }, []);

  // Initialize notification system, FCM, and background medication sync when user is authenticated
  React.useEffect(() => {
    const visibilityCleanups: Set<() => void> = new Set();
    
    const initializeServices = async () => {
      try {
        // Get current user ID
        const currentUser = auth.currentUser;
        if (currentUser) {
          const appLog = logger.createScope('App');
          
          // Initialize notification system
          NotificationIntegrationService.initialize(currentUser.uid);
          appLog.info('Notification System Initialized', { userId: currentUser.uid });
          
          // Check for unread notifications when app opens and show pop-up
          const checkAndShowUnreadNotifications = async () => {
            try {
              const NotificationService = (await import('./services/notificationService')).default;
              const { showGlobalInfo } = await import('./components/NotificationSystem');
              
              // Get unread notifications (limit to 50 to check recent ones)
              const notifications = await NotificationService.getNotifications(currentUser.uid, 50);
              const unreadNotifications = notifications.filter(n => !n.read);
              
              // Show pop-up for unread notifications created while app was closed
              // Only show for recent notifications (within last 24 hours) to avoid spam
              const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
              const recentUnread = unreadNotifications.filter(n => 
                n.createdAt >= oneDayAgo
              );
              
              if (recentUnread.length > 0) {
                // Group by type
                const labResults = recentUnread.filter(n => 
                  (n.type === 'lab_result' || n.category === 'records_processed') && 
                  (n.data?.isInformational === true || n.title?.includes('Processed'))
                );
                const vetVisits = recentUnread.filter(n => 
                  (n.type === 'vet_visit' || n.category === 'records_processed') && 
                  (n.data?.isInformational === true || n.title?.includes('Processed')) &&
                  !n.data?.isNormal
                );
                const attentionNeeded = recentUnread.filter(n => 
                  (n.priority === 'urgent' || n.priority === 'high' || n.category === 'health_alert') &&
                  !n.data?.isInformational
                );
                
                // Show pop-up for attention needed items first
                if (attentionNeeded.length > 0) {
                  const count = attentionNeeded.length;
                  showGlobalInfo(
                    count === 1 
                      ? attentionNeeded[0].message || attentionNeeded[0].title
                      : `You have ${count} item${count > 1 ? 's' : ''} that need your attention`,
                    count === 1 ? attentionNeeded[0].title : `⚠️ ${count} Items Need Attention`
                  );
                } else if (labResults.length > 0 || vetVisits.length > 0) {
                  // Show pop-up for processed documents
                  const total = labResults.length + vetVisits.length;
                  const items = [];
                  if (labResults.length > 0) {
                    items.push(`${labResults.length} lab result${labResults.length > 1 ? 's' : ''}`);
                  }
                  if (vetVisits.length > 0) {
                    items.push(`${vetVisits.length} vet visit${vetVisits.length > 1 ? 's' : ''}`);
                  }
                  
                  showGlobalInfo(
                    `${items.join(' and ')} ${total === 1 ? 'has' : 'have'} been processed and posted`,
                    `✅ New Health Records Available`
                  );
                }
              }
            } catch (error) {
              appLog.warn('Error checking for unread notifications on app open', { 
                error: error as Error,
                userId: currentUser.uid 
              });
            }
          };
          
          // Check immediately after a delay (for initial app open)
          setTimeout(checkAndShowUnreadNotifications, 3000);
          
          // Also check when app becomes visible again (user switches back to tab/window)
          const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
              // App became visible - check for new notifications
              setTimeout(checkAndShowUnreadNotifications, 1000);
            }
          };
          
          document.addEventListener('visibilitychange', handleVisibilityChange);
          
          // Store cleanup function
          const cleanup = () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
          visibilityCleanups.add(cleanup);
          
          // Initialize FCM (Firebase Cloud Messaging) for push notifications
          try {
            const FCMService = (await import('./services/fcmService')).default;
            
            // Check if FCM is supported
            if (FCMService.isSupported()) {
              // Initialize FCM
              FCMService.initialize();
              
              // Request permission and get token (non-blocking)
              setTimeout(async () => {
                try {
                  const token = await FCMService.requestPermission(currentUser.uid);
                  if (token) {
                    appLog.info('FCM token obtained', { userId: currentUser.uid });
                  } else {
                    appLog.debug('FCM permission not granted or token not available', { userId: currentUser.uid });
                  }
                } catch (fcmError) {
                  // Silently fail - don't interrupt user experience
                  appLog.warn('FCM initialization failed (non-critical)', { 
                    error: fcmError as Error,
                    userId: currentUser.uid
                  });
                }
              }, 2000); // Delay to not interfere with initial load
              
              // Setup foreground message listener
              FCMService.setupForegroundListener((payload) => {
                appLog.debug('Foreground FCM message received', { 
                  title: payload.notification?.title,
                  userId: currentUser.uid 
                });
                // The notification will be handled by the notification system
                // This is just for logging/debugging
              });
            } else {
              appLog.debug('FCM not supported in this browser', { userId: currentUser.uid });
            }
          } catch (fcmError) {
            // Silently fail - FCM is optional
            appLog.warn('FCM service not available (non-critical)', { 
              error: fcmError as Error,
              userId: currentUser.uid
            });
          }
          
          // Initialize background medication sync (runs silently, doesn't block UI)
          setTimeout(async () => {
            try {
              const { autoSyncMedicationsForUser } = await import('./services/backgroundMedicationSync');
              await autoSyncMedicationsForUser(currentUser.uid);
            } catch (syncError) {
              // Silently fail - don't interrupt user experience
              appLog.warn('Background medication sync failed (non-critical)', { 
                error: syncError as Error,
                userId: currentUser.uid
              });
            }
          }, 3000); // Delay to not interfere with initial load
          
          // DISABLED: Auto-dispatch test events (was creating hundreds of test notifications)
          // Test notifications should only be created manually via NotificationTest component
          // if (process.env.NODE_ENV === 'development') {
          //   setTimeout(() => {
          //     NotificationIntegrationService.dispatchTestEvents();
          //   }, 5000);
          // }
        }
      } catch (error) {
        logger.error('Service Initialization Failed', error as Error, { scope: 'App' });
      }
    };
    
    // Only initialize when auth state changes and user is available
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        initializeServices();
      }
    });
    
    return () => {
      unsubscribe();
      // Clean up all visibility change listeners
      visibilityCleanups.forEach(cleanup => cleanup());
      visibilityCleanups.clear();
    };
  }, []);

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log errors using logger
        logger.error('Application Error Caught', error, {
          componentStack: errorInfo.componentStack,
          scope: 'ErrorBoundary'
        });
        
        // In production, send to error tracking service
        // Example: Sentry.captureException(error, { extra: errorInfo });
      }}
    >
      <ToastProvider>
        <NotificationProvider maxNotifications={3}>
          <ErrorProvider>
            <CustomThemeProvider>
              <LayoutProvider>
                <AppRouter />
                {/* <NetworkStatusIndicator /> */}
              </LayoutProvider>
            </CustomThemeProvider>
          </ErrorProvider>
        </NotificationProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
